/**
 * Saved Reports Store
 *
 * Manages folders + reports with Supabase persistence.
 * Hybrid pattern: optimistic local update → async Supabase sync.
 */

import { create } from 'zustand';
import {
  type ReportVisibility,
  type SavedReport,
  type ReportFolder,
  createSavedReport,
  reportInsertRow,
  reportToSupabaseFormat,
  reportFromSupabaseFormat,
  folderToSupabaseFormat,
  folderFromSupabaseFormat,
} from '../../types/prioritization/savedReports';
import {
  isSupabaseAvailable,
  getSafeSupabaseClient,
  insertRecord,
  updateRecord,
  deleteRecord,
  getCurrentWorkspaceId,
} from '../../lib/supabaseSync';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { describeReportDependents, getReportDependents } from '../../lib/reportDependents';
import { useUserStore } from './userStore';
import type { FilterGroup, SortSpec } from '../../components/prioritization/item-list/hooks/useItemListState';
import type { ChartType } from '../../components/prioritization/item-list/components/chartTypes';
import type { QueryNode } from '../../lib/pql/types';

const REPORTS_TABLE = 'saved_reports';
const FOLDERS_TABLE = 'report_folders';

/** The report row both create paths build — they differ only in how they wait. */
function buildReport(params: CreateReportParams): SavedReport {
  return createSavedReport({
    workspaceId: getCurrentWorkspaceId() ?? undefined,
    folderId: params.folderId ?? null,
    name: params.name,
    description: params.description,
    entityType: 'items',
    filterGroups: params.filterGroups,
    queryText: params.queryText,
    queryAst: params.queryAst,
    sourceReportId: params.sourceReportId ?? null,
    sort: params.sort,
    groupBy: params.groupBy,
    visibleColumns: params.visibleColumns,
    activeChart: params.activeChart,
    isPinned: false,
    accessCount: 0,
    // The server stamps the real owner (migration 1023); this is the local row's
    // copy, so the person's own new report reads as theirs before a reload.
    createdBy: useUserStore.getState().userId,
    visibility: params.visibility ?? 'workspace',
  });
}

// ============================================================================
// State + Actions
// ============================================================================

interface SavedReportsState {
  folders: ReportFolder[];
  reports: SavedReport[];
  isLoading: boolean;
  error: string | null;
}

export interface CreateReportParams {
  name: string;
  description?: string;
  folderId?: string | null;
  filterGroups: FilterGroup[];
  /** Canonical PQL statement, when this report carries a full AST (e.g. a Smart Collection rule). */
  queryText?: string;
  queryAst?: QueryNode | null;
  /** The saved query this one was copied from, for a derived rule (migration 725). */
  sourceReportId?: string | null;
  sort: SortSpec[];
  groupBy: string | null;
  visibleColumns: string[];
  activeChart: ChartType;
  /** Who can see it. Unset means everyone in the workspace, as a rule or a feed always is. */
  visibility?: ReportVisibility;
}

interface SavedReportsActions {
  // Fetch
  fetchFromSupabase: () => Promise<void>;

  // Folder CRUD
  createFolder: (params: { name: string; parentId?: string | null; icon?: string }) => ReportFolder;
  renameFolder: (id: string, name: string) => void;
  deleteFolder: (id: string) => void;

  // Report CRUD
  createReport: (params: CreateReportParams) => SavedReport;
  /**
   * createReport, but it waits for the row to land and says whether it did.
   * Use it wherever the person is told the query was saved, and wherever
   * another row is about to reference the report — a Smart Collection rule
   * (collections.rule_query_id) or a pool feed (item_pools.feed_query_id) are
   * foreign keys, so the referenced row has to exist first. On failure the
   * optimistic local row is rolled back, so a failed save never looks saved.
   */
  createReportPersisted: (params: CreateReportParams) => Promise<{ report: SavedReport | null; error: string | null }>;
  updateReport: (id: string, updates: Partial<SavedReport>) => void;
  /**
   * Refuses while a Smart Collection rule or a pool feed still points at this
   * report: those FKs are ON DELETE SET NULL, so deleting it would silently
   * turn a smart collection static or leave a pool unfed with no trace.
   */
  deleteReport: (id: string) => { error: string | null };
  /**
   * Change who can see a report (set_saved_report_visibility, migration 1023).
   * It waits for the server's answer: only the owner may, and a report a Smart
   * Collection or a pool reads stays with the workspace.
   */
  setReportVisibility: (id: string, visibility: ReportVisibility) => Promise<{ error: string | null }>;
  moveReport: (id: string, folderId: string | null) => void;
  linkToSheets: (id: string, sheetsUrl: string) => void;
  recordAccess: (id: string) => void;

  // Selectors
  getRootFolders: () => ReportFolder[];
  getChildFolders: (parentId: string) => ReportFolder[];
  getReportsInFolder: (folderId: string | null) => SavedReport[];
  getPinnedReports: () => SavedReport[];
  getReportById: (id: string) => SavedReport | undefined;
}

// ============================================================================
// Store
// ============================================================================

export const useSavedReportsStore = create<SavedReportsState & SavedReportsActions>()(
  (set, get) => ({
    folders: [],
    reports: [],
    isLoading: false,
    error: null,

    // -------------------------------------------------------------------------
    // Fetch
    // -------------------------------------------------------------------------

    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) return;

      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      set({ isLoading: true, error: null });
      try {
        // Raw rows, NOT fetchAll: fetchAll camel-cases every row (fromDbFormat),
        // but the folder/report mappers read the wire shape (`created_at`,
        // `query_text`, `filters_json`, …). Fed camel-cased rows they got
        // `undefined` for every multi-word column — so since the first commit a
        // saved query reloaded from the database lost its query text, AST,
        // filters, sort, columns and lineage, its dates were Invalid Date, and
        // applying it from the Reports panel threw in recordAccess.
        const [foldersResult, reportsResult] = await Promise.all([
          supabase.from(FOLDERS_TABLE).select('*').order('created_at', { ascending: true }),
          supabase.from(REPORTS_TABLE).select('*').order('updated_at', { ascending: true }),
        ]);
        const fetchError = foldersResult.error ?? reportsResult.error;
        if (fetchError) {
          set({ isLoading: false, error: fetchError.message });
          return;
        }

        const folders = ((foldersResult.data ?? []) as Record<string, unknown>[]).map(folderFromSupabaseFormat);
        const reports = ((reportsResult.data ?? []) as Record<string, unknown>[]).map(reportFromSupabaseFormat);
        set({ folders, reports, isLoading: false });
      } catch (err) {
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to fetch reports' });
      }
    },

    // -------------------------------------------------------------------------
    // Folder CRUD
    // -------------------------------------------------------------------------

    createFolder: ({ name, parentId = null, icon }) => {
      const now = new Date();
      const folder: ReportFolder = {
        id: crypto.randomUUID(),
        workspaceId: getCurrentWorkspaceId() ?? undefined,
        name,
        icon,
        parentId: parentId ?? null,
        sortOrder: get().folders.filter((f) => f.parentId === (parentId ?? null)).length,
        createdAt: now,
        updatedAt: now,
      };

      set((s) => ({ folders: [...s.folders, folder] }));

      if (isSupabaseAvailable()) {
        insertRecord(FOLDERS_TABLE, folderToSupabaseFormat(folder)).then((r) => {
          if (r.error) console.error('Failed to save folder:', r.error);
        });
      }

      return folder;
    },

    renameFolder: (id, name) => {
      set((s) => ({
        folders: s.folders.map((f) => f.id === id ? { ...f, name, updatedAt: new Date() } : f),
      }));
      if (isSupabaseAvailable()) {
        updateRecord(FOLDERS_TABLE, id, { name, updated_at: new Date().toISOString() }).then((r) => {
          if (r.error) console.error('Failed to rename folder:', r.error);
        });
      }
    },

    deleteFolder: (id) => {
      // Move child reports to root, move child folders to parent
      const folder = get().folders.find((f) => f.id === id);
      if (!folder) return;

      set((s) => ({
        folders: s.folders
          .filter((f) => f.id !== id)
          .map((f) => f.parentId === id ? { ...f, parentId: folder.parentId, updatedAt: new Date() } : f),
        reports: s.reports.map((r) => r.folderId === id ? { ...r, folderId: null, updatedAt: new Date() } : r),
      }));

      if (isSupabaseAvailable()) {
        deleteRecord(FOLDERS_TABLE, id).then((r) => {
          if (r.error) console.error('Failed to delete folder:', r.error);
        });
      }
    },

    // -------------------------------------------------------------------------
    // Report CRUD
    // -------------------------------------------------------------------------

    createReport: (params) => {
      const report = buildReport(params);

      set((s) => ({ reports: [...s.reports, report] }));

      if (isSupabaseAvailable()) {
        insertRecord(REPORTS_TABLE, reportInsertRow(report)).then((r) => {
          if (r.error) console.error('Failed to save report:', r.error);
        });
      }

      return report;
    },

    createReportPersisted: async (params) => {
      const report = buildReport(params);

      set((s) => ({ reports: [...s.reports, report] }));

      // No backend configured (demo/offline): the local row is all there is,
      // and that is the same promise createReport has always made.
      if (!isSupabaseAvailable()) return { report, error: null };

      const res = await insertRecord(REPORTS_TABLE, reportInsertRow(report));
      if (res.error) {
        set((s) => ({ reports: s.reports.filter((r) => r.id !== report.id) }));
        return { report: null, error: res.error };
      }
      return { report, error: null };
    },

    updateReport: (id, updates) => {
      set((s) => ({
        reports: s.reports.map((r) =>
          r.id === id ? { ...r, ...updates, updatedAt: new Date() } : r
        ),
      }));

      const updated = get().reports.find((r) => r.id === id);
      if (updated && isSupabaseAvailable()) {
        updateRecord(REPORTS_TABLE, id, reportToSupabaseFormat(updated)).then((r) => {
          if (r.error) console.error('Failed to update report:', r.error);
        });
      }
    },

    deleteReport: (id) => {
      const blocked = describeReportDependents(getReportDependents(id));
      if (blocked) return { error: blocked };

      set((s) => ({ reports: s.reports.filter((r) => r.id !== id) }));
      if (isSupabaseAvailable()) {
        deleteRecord(REPORTS_TABLE, id).then((r) => {
          if (r.error) console.error('Failed to delete report:', r.error);
        });
      }
      return { error: null };
    },

    setReportVisibility: async (id, visibility) => {
      if (isSupabaseAvailable()) {
        const { error } = await getTypedSupabaseClient().rpc('set_saved_report_visibility', {
          p_report_id: id,
          p_visibility: visibility,
        });
        if (error) return { error: error.message };
      }
      set((s) => ({
        reports: s.reports.map((r) => (r.id === id ? { ...r, visibility, updatedAt: new Date() } : r)),
      }));
      return { error: null };
    },

    moveReport: (id, folderId) => {
      get().updateReport(id, { folderId });
    },

    linkToSheets: (id, sheetsUrl) => {
      get().updateReport(id, { sheetsUrl });
    },

    recordAccess: (id) => {
      const report = get().reports.find((r) => r.id === id);
      if (report) {
        get().updateReport(id, {
          accessCount: (report.accessCount ?? 0) + 1,
          lastAccessedAt: new Date(),
        });
      }
    },

    // -------------------------------------------------------------------------
    // Selectors
    // -------------------------------------------------------------------------

    getRootFolders: () =>
      get().folders.filter((f) => !f.parentId).sort((a, b) => a.sortOrder - b.sortOrder),

    getChildFolders: (parentId) =>
      get().folders.filter((f) => f.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder),

    getReportsInFolder: (folderId) =>
      get().reports.filter((r) => r.folderId === folderId),

    getPinnedReports: () =>
      get().reports.filter((r) => r.isPinned),

    getReportById: (id) =>
      get().reports.find((r) => r.id === id),
  })
);

export default useSavedReportsStore;
